import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';
import { UpdateContactData } from '../interfaces/update-contact-data';
import { firstValueFrom } from 'rxjs';
import { Pagador } from '../interfaces/pagador';
import { UserRoles } from 'src/app/shared/enums/roles-enum';

@Injectable({
  providedIn: 'root'
})
export class PagadorService {

  API_URL = environment.apiGatewayURL;
  constructor(private http: HttpClient, private cognito: CognitoService) { }
  async createPagador(body: any){
    const userLogged = await this.cognito.getUser();
    let userBody = {...body, username: userLogged.username};
    if(userLogged.attributes['custom:user-group']!=UserRoles.aguacate_operator){
      userBody = {...body, email: userLogged.attributes.email};
    }
    // const userBody = {...body, email: userLogged.attributes.email, username: userLogged.username};
    const serviceURL = `${this.API_URL}pagador/create`
    // Propaga el error: el caller muestra el mensaje del backend (documento ya
    // registrado, RUT inválido, creación no habilitada).
    return firstValueFrom(this.http.post(serviceURL, userBody));
  }

  //TODO separar logica de edicion de imagenes de la edicion de pagador
  /**
   * Propaga el error HTTP (antes se lo tragaba y el formulario solo decía "No
   * se ha podido actualizar"): el caller muestra el mensaje del backend, p. ej.
   * el 400 cuando el cliente intenta cambiar la identidad del pagador.
   */
  async editarPagador(body: any){
    const userLogged = await this.cognito.getUser();
    const userBody = {...body, email: userLogged.attributes.email, username: userLogged.username};
    const serviceURL = `${this.API_URL}pagador/edit`
    return firstValueFrom(this.http.post(serviceURL, userBody));
  }

  getUserData(email: string){
    const serviceURL = `${this.API_URL}user/${email}`;
    return this.http.get(serviceURL);
  }

  getPagadoresByUser(usuarioId: number): Promise<Pagador[]>{
    const serviceURL = `${this.API_URL}pagador/by-user/${usuarioId}`
    return firstValueFrom(this.http.get<Pagador[]>(serviceURL));
  }

  getPagador(pagadorId: string): Promise<Pagador>{
    const serviceURL = `${this.API_URL}pagador/by-id/${pagadorId}`
    return firstValueFrom(this.http.get<Pagador>(serviceURL));
  }

  /** Operador: todos los pagadores con su compliance (ordenados: pendientes primero). */
  async getPagadoresCompliance(): Promise<Pagador[]>{
    const serviceURL = `${this.API_URL}pagador/compliance`;
    // El backend responde { pagadores: [...] } (successResponse), no un array pelado.
    const res = await firstValueFrom(
      this.http.get<{ pagadores: Pagador[] }>(serviceURL),
    );
    return res?.pagadores ?? [];
  }

  updateUserData(payload: UpdateContactData){
    const serviceURL = `${this.API_URL}userContactData`;
    return this.http.put(serviceURL, payload);
  }

  inactivatePagador(pagadorId: string){
    const serviceURL = `${this.API_URL}pagador/inactivate`;
    return firstValueFrom(this.http.post(serviceURL, {pagadorId}));
  }

  updateUserCompliance(pagadorId: string, emailOperator: string, compliance: any){
    const serviceURL = `${this.API_URL}compliance-pagador`;
    const payload = {
      pagadorId, emailOperator, compliance
    }
    return this.http.put(serviceURL, payload)
  }

  updateUserChecks(pagadorId: string, emailOperator: string, compliance: any){
    const serviceURL = `${this.API_URL}checks-pagador`;
    const payload = {
      pagadorId, emailOperator, compliance
    }
    return this.http.put(serviceURL, payload)
  }

  /**
   * Operador: bloquea un pagador (estado de compliance 4) en Nest. No se borra:
   * queda con log de quién y por qué, y deja de contar en las validaciones de
   * RUT (no recibe abonos). Se desbloquea aprobándolo/rechazándolo, si su
   * documento sigue libre.
   */
  bloquearPagador(pagadorId: string, motivo?: string): Promise<{ pagadorId: string; estadoComplianceID: number; yaEstabaBloqueado: boolean }> {
    // No va bajo `/admin`: el load balancer de producción corta esas rutas.
    const serviceURL = `${environment.apiNest}pagadores-control/${encodeURIComponent(pagadorId)}/bloquear`;
    return firstValueFrom(
      this.http.post<{ pagadorId: string; estadoComplianceID: number; yaEstabaBloqueado: boolean }>(
        serviceURL,
        motivo ? { motivo } : {},
      ),
    );
  }

  /**
   * ¿El RUT está libre para un pagador? Con el tipo de pagador y de documento
   * el back compara en la misma familia que el alta (un RUT y una cédula
   * venezolana con los mismos dígitos no son el mismo documento).
   */
  checkPayerExist(rut: string, tipo: { tipoUsuarioId?: number; tipoId?: string | null } = {}){
    const serviceURL = `${this.API_URL}pagador/check-payer/${rut}`;
    const params: Record<string, string> = {};
    if (tipo.tipoUsuarioId !== undefined && tipo.tipoUsuarioId !== null) params['tipoUsuarioId'] = String(tipo.tipoUsuarioId);
    if (tipo.tipoId) params['tipoId'] = String(tipo.tipoId);
    return firstValueFrom(this.http.get(serviceURL, { params }));
  }
}
